"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePaymentInfo = void 0;
/*
Copyright (C) 2009 - 2021 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
const react_1 = require("react");
const lodash_1 = require("lodash");
const use_fetch_customer_1 = require("./use-fetch-customer");
const use_list_payment_accounts_1 = require("./use-list-payment-accounts");
/**
 * This hook sets up the state and info related to a payment form including customer info,
 * saved payment accounts, and initial card values.
 *
 * If saved payments are represented using SavedPaymentMethods and stored in
 * PaymentTransactionServices, {@link #useSavedPaymentMethodInfo} should be used instead.
 *
 * @deprecated In favor of using {@link #useSavedPaymentMethodInfo} and PaymentTransactionServices to manage saved payment methods.
 */
const usePaymentInfo = (props) => {
    const { authState } = props;
    const { isAuthenticated, isAuthenticating } = authState;
    const { customer } = (0, use_fetch_customer_1.useFetchCustomer)(props);
    const { paymentAccounts: payments, isFetching: loadingPayments } = (0, use_list_payment_accounts_1.useListPaymentAccounts)(props);
    const defaultPayment = useUserDefaultPayment(payments, customer);
    const [selectedPayment, setSelectedPayment] = (0, react_1.useState)(defaultPayment || {});
    const initialCardValues = useInitialCardValues(selectedPayment);
    (0, react_1.useEffect)(() => {
        if (isAuthenticating || loadingPayments) {
            return;
        }
        if (!isAuthenticated || (0, lodash_1.isEmpty)(payments)) {
            setSelectedPayment({});
            return;
        }
        setSelectedPayment(defaultPayment || {});
    }, [
        defaultPayment,
        isAuthenticated,
        isAuthenticating,
        loadingPayments,
        payments
    ]);
    return (0, react_1.useMemo)(() => ({
        customer,
        initialCardValues,
        payments,
        selectedPayment,
        setSelectedPayment
    }), [customer, initialCardValues, payments, selectedPayment]);
};
exports.usePaymentInfo = usePaymentInfo;
const useUserDefaultPayment = (payments, customer) => {
    const [payment, setPayment] = (0, react_1.useState)();
    (0, react_1.useEffect)(() => {
        setPayment((0, lodash_1.find)(payments, ['id', customer === null || customer === void 0 ? void 0 : customer.defaultPaymentAccountId]) ||
            (0, lodash_1.head)(payments));
    }, [customer, payments]);
    return payment;
};
const EmptyCardValues = {
    savedPaymentMethodId: '',
    cardNumber: '',
    nameOnAccount: '',
    cardType: '',
    expirationMonth: new Date().getMonth() + 1,
    expirationYear: new Date().getUTCFullYear(),
    creditCardSecCode: '',
    savePaymentForFutureUse: false,
    amountToApply: 0
};
const useInitialCardValues = (selectedPayment) => {
    const [values, setValues] = (0, react_1.useState)(EmptyCardValues);
    (0, react_1.useEffect)(() => {
        if ((0, lodash_1.isEmpty)(selectedPayment)) {
            setValues(EmptyCardValues);
            return;
        }
        const { creditCardHolder = '', creditCardNumber = '', creditCardType = '', creditCardExpDateYear = '', creditCardExpDateMonth = '' } = selectedPayment.displayAttributes || {};
        setValues({
            savedPaymentMethodId: selectedPayment.id || '',
            cardNumber: creditCardNumber,
            nameOnAccount: creditCardHolder,
            cardType: creditCardType,
            expirationMonth: creditCardExpDateMonth
                ? Number(creditCardExpDateMonth)
                : new Date().getMonth() + 1,
            expirationYear: creditCardExpDateYear
                ? Number(creditCardExpDateYear)
                : new Date().getUTCFullYear(),
            creditCardSecCode: '',
            savePaymentForFutureUse: false,
            amountToApply: 0
        });
    }, [selectedPayment]);
    return values;
};
//# sourceMappingURL=use-payment-info.js.map